
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// RTK slices
import { setToken, setUserIdentity, resetInitialization, setInitializing, setInitialized } from './redux/slices/appSlice';
import { authFetch } from './utils/authFetch';

import { Route, Routes, Navigate } from 'react-router-dom';
import { AppShell, Transition } from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import EnhancedLoadingScreen from './components/EnhancedLoadingScreen.js';
import UniversalFileUploader from './UniversalFileUploader.jsx';
import {jwtDecode} from 'jwt-decode';
import { isRiskManager, hasAuthorizedRole } from './utils/roleHelpers';
import { Header } from './components/Header';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import './App.css';

// Agency (lazy-loaded)
const AgencyLayout = React.lazy(() => import('./agency/components/AgencyLayout'));
const AgencyHome = React.lazy(() => import('./agency/pages/AgencyHome'));
const AgencyDashboard = React.lazy(() => import('./agency/pages/AgencyDashboard'));
const CreateAssessmentPage = React.lazy(() => import('./agency/pages/CreateAssessment'));
const AssessmentDetail = React.lazy(() => import('./agency/pages/AssessmentDetail'));
const AgencyFindingsPage = React.lazy(() => import('./agency/pages/AgencyFindingsPage'));
const AssessmentRisksPage = React.lazy(() => import('./agency/pages/AssessmentRisksPage'));
const AssessmentControlsPage = React.lazy(() => import('./agency/pages/AssessmentControlsPage'));
const AssessmentActionsPage = React.lazy(() => import('./agency/pages/AssessmentActionsPage'));
const AssessmentResidualPage = React.lazy(() => import('./agency/pages/AssessmentResidualPage'));
const AssessmentSecurityPage = React.lazy(() => import('./agency/pages/AssessmentSecurityPage'));
const AssessmentReviewPage = React.lazy(() => import('./agency/pages/AssessmentReviewPage'));
const AgencyReportPage = React.lazy(() => import('./agency/pages/AgencyReportPage'));
const AgencyReportsList = React.lazy(() => import('./agency/pages/AgencyReportsList'));
const AgencyDashboardPage = React.lazy(() => import('./agency/pages/AgencyDashboardPage'));
const AgencyAgentsPage = React.lazy(() => import('./agency/pages/AgencyAgentsPage'));
const TeamDashboard = React.lazy(() => import('./agency/pages/TeamDashboard'));
const SkillsHome = React.lazy(() => import('./skillManager/SkillsHome'));
const SkillEditor = React.lazy(() => import('./skillManager/SkillEditor'));
const MethodsHome = React.lazy(() => import('./methodManager/MethodsHome'));
const MethodEditor = React.lazy(() => import('./methodManager/MethodEditor'));
const SimplifiedAssessmentLaunch = React.lazy(() => import('./agency/pages/SimplifiedAssessmentLaunch'));
const ToolsSkillsPage = React.lazy(() => import('./agency/pages/ToolsSkillsPage'));
const FrameworksPage = React.lazy(() => import('./agency/pages/FrameworksPage'));
const RiskCataloguesPage = React.lazy(() => import('./agency/pages/RiskCataloguesPage'));
const OrganisationPage = React.lazy(() => import('./agency/pages/OrganisationPage'));
const HuginnSoulPage = React.lazy(() => import('./agency/pages/HuginnSoulPage'));
const IntegrationsPage = React.lazy(() => import('./agency/pages/IntegrationsPage'));
const RiskTaxonomyPage = React.lazy(() => import('./agency/pages/RiskTaxonomyPage'));
const ThreatAgentsPage = React.lazy(() => import('./agency/pages/ThreatAgentsPage'));
const ContextLibraryPage = React.lazy(() => import('./agency/pages/ContextLibraryPage'));
const AdminPage = React.lazy(() => import('./agency/pages/AdminPage'));

function App() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently} = useAuth0();

  const appInitializing = useSelector(state => state.app.isInitializing);
  const token = useSelector(state => state.app.token);
  const userIdentity = useSelector(state => state.app.userIdentity);
  const identityLoaded = useSelector(state => state.app.identityLoaded);

  const [userRoles, setUserRoles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);
  const showInitialLoadingScreen = isLoading || appInitializing;
  const userIsRiskManager = useMemo(() => isRiskManager(userRoles), [userRoles]);
  const userIsAuthorized = useMemo(() => hasAuthorizedRole(userRoles), [userRoles]);
  const dispatch = useDispatch();
  useInactivityTimeout();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        dispatch(resetInitialization());
        dispatch(setInitializing(true));

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'read:models write:models delete:models'
          }
        });

        dispatch(setToken(token));
        dispatch(setInitialized(true));
      } catch (error) {
        console.error('Error initializing app:', error);
        dispatch(setInitialized(true));
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    if (isAuthenticated) {
      initializeApp();
    }
  }, [dispatch, getAccessTokenSilently, isAuthenticated, logout]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai', 
            scope: 'openid profile email read:models write:models delete:models' 
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserRoles(roles);
        console.log('User roles:', roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [isAuthenticated, getAccessTokenSilently]);





  // Resolve identity + tenancy once the token is available. agency-whoami records
  // the user in the platform roster and returns their org (null until an admin
  // assigns one) plus isPlatformAdmin.
  useEffect(() => {
    const loadIdentity = async () => {
      try {
        const res = await authFetch('/.netlify/functions/agency-whoami');
        if (!res.ok) throw new Error(`whoami failed (${res.status})`);
        const data = await res.json();
        dispatch(setUserIdentity(data));
      } catch (error) {
        console.error('Error loading identity:', error);
        // Mark loaded with no org so the UI can show the awaiting-access state
        // rather than spinning forever.
        dispatch(setUserIdentity(null));
      }
    };
    if (isAuthenticated && token) {
      loadIdentity();
    }
  }, [isAuthenticated, token, dispatch]);

  useEffect(() => {
    if (!showInitialLoadingScreen) {
      setFadeIn(true);
    }
  }, [showInitialLoadingScreen]);

  // Only show the animated loading screen during initial app load
  // Subsequent data refreshes (e.g., after creating assessment) should not show this
  if (showInitialLoadingScreen) {
    return (
      <EnhancedLoadingScreen
        onComplete={() => {
          // Ensure we stop showing the loading screen immediately after chat completes
          // by flipping modelsLoading false if the fetch already finished.
          // If still loading, fallback to state change from reducer.
        }}
      />
    );
  }

  return (
    <div className="App">
    <AppShell
      header={{ height: 68 }}
      padding="md"
      styles={{
        main: { backgroundColor: '#F5F5F5' }
      }}
    >
      <AppShell.Header>
        <Header
          user={user}
          isAuthenticated={isAuthenticated}
          userRoles={userRoles}
          userIsRiskManager={userIsRiskManager}
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </AppShell.Header>

      <AppShell.Main>
        <Transition mounted={fadeIn} transition="fade" duration={800} timingFunction="ease">
          {(styles) => (
            <div style={styles}>
              {/* Gate: only users with an explicit role can access the app */}
              {isAuthenticated && userRoles.length > 0 && !userIsAuthorized ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 600 }}>Access Denied</h2>
                    <p style={{ color: '#868e96', margin: '0 0 16px' }}>
                      Your account does not have an authorized role. Contact your administrator to request access.
                    </p>
                    <button
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : isAuthenticated && identityLoaded && !userIdentity?.orgId && !userIdentity?.isPlatformAdmin ? (
                /* Gate: authenticated but not yet assigned to an org */
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 600 }}>Awaiting access</h2>
                    <p style={{ color: '#868e96', margin: '0 0 16px', maxWidth: 420 }}>
                      Your account isn’t assigned to an organisation yet. An administrator
                      will add you shortly — please check back soon.
                    </p>
                    <button
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : (
              <Routes>
          {/* Top-level routes (no sidebar) */}
          <Route path="/" element={
            userIsRiskManager ? (
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
                <AgencyDashboardPage user={user} userRoles={userRoles} />
              </React.Suspense>
            ) : (
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
                <TeamDashboard user={user} />
              </React.Suspense>
            )
          } />
          {/* /dashboard is the canonical URL for the merged Service Desk +
              Risk Landscape page. /landscape is preserved as an alias for
              backward compatibility with old bookmarks. */}
          <Route path="/dashboard" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Dashboard...</div>}>
              <AgencyDashboardPage user={user} userRoles={userRoles} />
            </React.Suspense>
          } />
          <Route path="/landscape" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Dashboard...</div>}>
              <AgencyDashboardPage user={user} userRoles={userRoles} />
            </React.Suspense>
          } />
          {/* Assessments — top-level */}
          <Route path="/assessments" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyDashboard />
            </React.Suspense>
          } />
          <Route path="/assessments/new" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <CreateAssessmentPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentDetail />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/findings" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyFindingsPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/risks" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentRisksPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/controls" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentControlsPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/actions" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentActionsPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/residual" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentResidualPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/security" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentSecurityPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/review" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentReviewPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/report" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyReportPage />
            </React.Suspense>
          } />

          {/* Simplified assessment launch (Team Member flow) */}
          <Route path="/start/:type" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <SimplifiedAssessmentLaunch />
            </React.Suspense>
          } />

          {/* Reports — agency-native canvas */}
          <Route path="/reports" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Reports...</div>}>
              <AgencyReportsList />
            </React.Suspense>
          } />

          {/* Universal uploader entrypoint (also reused by /agency/policies with a filter) */}
          <Route path="/universal-uploader" element={<UniversalFileUploader />} />

          {/* Agency routes (with sidebar, lazy-loaded) */}
          <Route path="/agency" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Agency...</div>}>
              <AgencyLayout />
            </React.Suspense>
          }>
            <Route index element={<AgencyHome />} />
            <Route path="admin" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Admin…</div>}>
                <AdminPage />
              </React.Suspense>
            } />
            <Route path="teams/risk" element={<AgencyDashboard />} />
            <Route path="teams/security" element={<AgencyDashboard />} />
            <Route path="teams/audit" element={<AgencyDashboard />} />
            <Route path="agents" element={<AgencyAgentsPage />} />
            <Route path="policies" element={<UniversalFileUploader categoryFilter={['policy-governance', 'policy-security', 'policy-ai-control']} />} />
            <Route path="frameworks" element={<FrameworksPage />} />
            <Route path="risk-catalogues" element={<RiskCataloguesPage />} />
            <Route path="organisation" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <OrganisationPage />
              </React.Suspense>
            } />
            <Route path="huginn-soul" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <HuginnSoulPage />
              </React.Suspense>
            } />
            <Route path="integrations" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <IntegrationsPage />
              </React.Suspense>
            } />
            <Route path="risk-taxonomy" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <RiskTaxonomyPage />
              </React.Suspense>
            } />
            <Route path="threat-agents" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ThreatAgentsPage />
              </React.Suspense>
            } />
            <Route path="context-library" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ContextLibraryPage />
              </React.Suspense>
            } />
            {/* Backwards compatible redirect: /agency/knowledge-base → Organisation */}
            <Route path="knowledge-base" element={<Navigate to="/agency/organisation" replace />} />
            <Route path="tools" element={<ToolsSkillsPage />} />
            <Route path="skills" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Skills...</div>}>
                <SkillsHome />
              </React.Suspense>
            } />
            <Route path="skills/:skillKey" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Skill...</div>}>
                <SkillEditor />
              </React.Suspense>
            } />
            <Route path="methods" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Methods...</div>}>
                <MethodsHome />
              </React.Suspense>
            } />
            <Route path="methods/:methodKey" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Method...</div>}>
                <MethodEditor />
              </React.Suspense>
            } />
          </Route>

          <Route path="*" element={<div>Not Found</div>} />
          </Routes>
              )}
            </div>
          )}
        </Transition>
      </AppShell.Main>
    </AppShell>
    </div>

  );
}

export default App;
